import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { cn } from "../../lib/cn";
import { Drawer } from "../Drawer";
import { Sidebar } from "./Sidebar";
import { BottomNav } from "./BottomNav";
import type { NavGroup, NavItem, SidebarRenderLinkArgs } from "./Sidebar";
import type { BottomNavItem, BottomNavRenderLinkArgs } from "./BottomNav";

export interface AppShellProps {
  /** Grupos de navegação da sidebar. */
  navigation: NavGroup[];
  activeId?: string;
  /** Marca (logo + nome) no topo da sidebar. */
  brand?: ReactNode;
  /** Rodapé da sidebar: status agregado dos monitores + conta. */
  sidebarFooter?: ReactNode;
  /** `<Topbar />` já configurada. O shell injeta o `onOpenMenu` do mobile. */
  topbar: (args: { onOpenMenu: () => void }) => ReactNode;
  /** Até 5 itens — barra inferior do mobile. Omitir desliga a barra. */
  bottomNav?: BottomNavItem[];
  onNavigate?: (item: NavItem | BottomNavItem) => void;
  renderLink?: (args: SidebarRenderLinkArgs) => ReactNode;
  renderBottomLink?: (args: BottomNavRenderLinkArgs) => ReactNode;
  /** Conteúdo da página. */
  children: ReactNode;
  /** `false` quando a página precisa da largura toda (tabela muito larga). */
  constrained?: boolean;
  className?: string;
}

/**
 * Esqueleto do app: canvas cinza com a sidebar e a topbar flutuando como
 * PAINÉIS brancos (raio 16, a 16px das bordas, sem borda nem sombra — o branco
 * sobre o cinza já separa os planos) + área de conteúdo de até 1440px.
 *
 * O que colapsa no mobile (390x844):
 *   - a sidebar some e vira Drawer (abre pelo hambúrguer da topbar);
 *   - a topbar cai de 64px para 48px e encosta nas bordas (sem moldura);
 *   - aparece a BottomNav com até 5 destinos;
 *   - o conteúdo perde a margem lateral grande (px-3 em vez de px-4/6).
 */
export function AppShell({
  navigation,
  activeId,
  brand,
  sidebarFooter,
  topbar,
  bottomNav,
  onNavigate,
  renderLink,
  renderBottomLink,
  children,
  constrained = true,
  className,
}: AppShellProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  const openMenu = useCallback(() => setMenuOpen(true), []);

  // navegou => o drawer fecha; deixar aberto engole a tela inteira no celular
  const handleNavigate = useCallback(
    (item: NavItem | BottomNavItem) => {
      setMenuOpen(false);
      onNavigate?.(item);
    },
    [onNavigate],
  );

  // trocou para desktop com o drawer aberto: fecha para não ficar preso
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    // oxlint-disable-next-line eslint-js/no-restricted-syntax -- breakpoint md do Tailwind (48rem) em matchMedia, que não lê token CSS
    const query = window.matchMedia("(min-width: 768px)");
    const onChange = (event: MediaQueryListEvent) => {
      if (event.matches) setMenuOpen(false);
    };
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return (
    <div className={cn("flex min-h-dvh bg-background text-foreground md:gap-4 md:p-4", className)}>
      <a
        href="#conteúdo"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded-pill focus:bg-surface-raised focus:px-4 focus:py-2 focus:text-sm focus:shadow-md"
      >
        Pular para o conteúdo
      </a>

      {/* sidebar fixa: some abaixo de md */}
      {/* painel flutuante: 100dvh menos os 16px de cima e de baixo */}
      <aside className="sticky top-4 hidden h-[calc(100dvh-2rem)] shrink-0 md:block">
        <Sidebar
          groups={navigation}
          {...(activeId !== undefined ? { activeId } : {})}
          {...(brand ? { brand } : {})}
          {...(sidebarFooter ? { footer: sidebarFooter } : {})}
          onNavigate={handleNavigate}
          {...(renderLink ? { renderLink } : {})}
        />
      </aside>

      {/* mobile: a MESMA sidebar dentro de um drawer */}
      <Drawer
        open={menuOpen}
        onOpenChange={setMenuOpen}
        side="left"
        title="Navegação"
        widthClassName="w-[85vw] max-w-[20rem]"
        className="md:hidden"
      >
        <Sidebar
          groups={navigation}
          {...(activeId !== undefined ? { activeId } : {})}
          {...(sidebarFooter ? { footer: sidebarFooter } : {})}
          showCollapseToggle={false}
          collapsed={false}
          onNavigate={handleNavigate}
          {...(renderLink ? { renderLink } : {})}
          className="w-full rounded-none"
        />
      </Drawer>

      <div className="flex min-w-0 flex-1 flex-col md:gap-4">
        {topbar({ onOpenMenu: openMenu })}

        <main
          id="conteúdo"
          tabIndex={-1}
          className={cn(
            "min-w-0 flex-1 px-3 py-4 focus-visible:outline-none md:px-1 md:pb-6 md:pt-2",
            constrained && "w-full max-w-content",
          )}
        >
          {children}
        </main>

        {bottomNav && bottomNav.length > 0 ? (
          <BottomNav
            items={bottomNav}
            {...(activeId !== undefined ? { activeId } : {})}
            onNavigate={handleNavigate}
            {...(renderBottomLink ? { renderLink: renderBottomLink } : {})}
          />
        ) : null}
      </div>
    </div>
  );
}
